import {
  FamilyApplicationResp,
  FamilyAttestation,
  Student,
} from '@/apis/family_application/requests'
import {
  selectApplicationRequestStatus,
  UpdateFamilyAttestation
} from '@/app-storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { useAppAlert } from '@/app/AppAlertProvider'
import { parseRequestError } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { FamilyApplicationQueries } from '@/queries/family_applications/queries.ts'
import { AppDispatch } from '@/storage/store'
import { MutationStatus, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import sanitizedHtml from 'sanitize-html'
import { ATTESTATION_STATUS } from '../../family_application/utils'

type Props = {
  application: FamilyApplicationResp
  selectedStudent: Student
  onAttestationsFinished: () => void
  studentFlagRequestStatus: MutationStatus
}

export function AttestionsPage({
  application,
  selectedStudent,
  onAttestationsFinished,
  studentFlagRequestStatus,
}: Props) {
  const studentAttestations = application.attestations.filter((attestation) => {
    return attestation.student_id === selectedStudent.id
  })
  const dispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()

  const [currentAttestation, setCurrentAttestation] = useState<
    number | undefined
  >(() => {
    if (studentAttestations.length > 0) {
      return studentAttestations[0].id
    } else {
      return undefined
    }
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentAttestation])
  const { t: translate } = useTranslation()
  const ProgramId = useSelector(selectProgramID)

  const ApplicationStatus = useSelector(selectApplicationRequestStatus)
  const attestationTextQuery = useQuery(
    FamilyApplicationQueries.fetchAcctAttestationsText(ProgramId, {
      staleTime: Infinity,
    })
  )

  function getCurrentAttestationIndex(
    studentAttestations: FamilyAttestation[]
  ) {
    return studentAttestations.findIndex((att) => att.id === currentAttestation)
  }

  async function handleAttestationConfirmation() {
    const currentAttestationIndex =
      getCurrentAttestationIndex(studentAttestations)

    const response = await dispatch(UpdateFamilyAttestation({
      attestation: {
        id: selectedAttestation?.id as number,
        status: ATTESTATION_STATUS.CONFIRMED

      },
      family_account_id: application.family_account_id,
    }))

    if (UpdateFamilyAttestation.fulfilled.match(response)) { 

      const nextAttestation = studentAttestations[currentAttestationIndex + 1]
      if (nextAttestation) {
        setCurrentAttestation(nextAttestation.id)
      } else {
        onAttestationsFinished()
      }
    } else {
      const parsedErrorMessage = parseRequestError(response.error)
      fireAlert({
        variant: "error",
        confirmBtn: true,
        message: parsedErrorMessage.message
      })
    }
  }

  if (attestationTextQuery.isError) {
    return <div>{attestationTextQuery.error.message}</div>
  }
  if (attestationTextQuery.isPending) return <></>

  const sanitizeText = (text: string) => {
    const defaultOptions = {
      allowedAttributes: {
        a: ['href', 'class', 'target'],
      },
    }

    return (
      <p
        className="mb-1 text-gray-800 text-sm"
        dangerouslySetInnerHTML={{
          __html: sanitizedHtml(text, defaultOptions),
        }}
      ></p>
    )
  }

  const selectedAttestation = attestationTextQuery.data.find((attestation) => {
    return attestation.id === currentAttestation
  })

  if (!selectedAttestation) {
    onAttestationsFinished()
    return null
  }

  const isLastAttestation =
    getCurrentAttestationIndex(studentAttestations) ===
    studentAttestations.length - 1

  return (
    <div className="">
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header className='md:h-full' >
          <h1 className="text-2xl font-medium text-gray-900 mb-10 sm:mb-5">
            {translate(selectedAttestation.code ?? 'almost_done')}
          </h1>
          <p className="text-sm text-gray-700 font-normal">
            {translate('complete_your_application')}:
          </p>
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <section aria-labelledby="attestation-title" className="my-10">
            <h2 id="attestation-title" className="sr-only">
              Attestation Details
            </h2>
            <div className="mb-3">
              <h3 className="font-medium text-gray-800 text-lg mb-1">
                {translate('i_acknowledge_that')}:
              </h3>
              <p className="text-gray-800">
                {sanitizeText(selectedAttestation.attestation_text[0])}
              </p>
            </div>

            <div className="bg-orange-100 mb-4 rounded-md p-2 mt-8">
              <h3 className="font-medium text-gray-800 text-lg mb-1">
                {translate('what_does_this_mean')}
              </h3>

              {sanitizeText(selectedAttestation.attestation_text[1])}
            </div>
          </section>

          <p
            className="text-center font-medium text-sm text-gray-800 mb-2"
            aria-live="polite"
          >
            {translate('attestation_progress', {
              current: getCurrentAttestationIndex(studentAttestations) + 1,
              total: studentAttestations.length,
            })}
          </p>
        </FamilyWizardLayout.Content>
        <FamilyWizardLayout.ActionBar>
          <div className="w-full">
            {isLastAttestation && (
              <p className="text-sm text-gray-600 max-w-md text-center mx-auto mb-2">
                By signing this application, I hereby certify that all
                information contained in this application is accurate and
                truthful.
              </p>
            )}
            <SubmitButton
              onClick={() => handleAttestationConfirmation()}
              status={
                studentFlagRequestStatus !== 'idle'
                  ? studentFlagRequestStatus
                  : ApplicationStatus
              }
              buttonTxt={
                isLastAttestation
                  ? translate('submit_application')
                  : translate('agree')
              }
              aria-label={
                isLastAttestation
                  ? translate('submit_application')
                  : translate('agree_to_attestation')
              }
            />
          </div>
        </FamilyWizardLayout.ActionBar>
      </FamilyWizardLayout>
    </div>
  )
}
